import { LogOut, UserRound } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import type { UserProfile } from '@/lib/types'

/**
 * The student's account menu - identity, their profile, and sign out.
 *
 * It replaces a bare icon button that sat next to the avatar (IELTS-506). That
 * button was the app's only way out of a session, and it was both unlabelled
 * and the first thing the narrow-window rules hid, which is how a student
 * ended up with no way to sign out at all. Sign out now lives behind the
 * avatar, where people look for it, and travels with the avatar to every
 * viewport.
 *
 * Not a `role="menu"`: a WAI-ARIA menu promises arrow-key roving focus over
 * menuitems, and this is identity text plus a link and a button. It is a
 * disclosure, the same contract `AdminAccountMenu` and `NotificationsBell`
 * use - `aria-expanded` on the trigger, a labelled region, ordinary Tab order.
 */

/** Display name that never renders an empty heading. */
function accountName(profile: UserProfile): string {
  return profile.full_name?.trim()
    || `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    || profile.user.email
    || 'Your account'
}

function accountInitials(profile: UserProfile): string {
  const first = profile.first_name?.trim()?.[0] || ''
  const last = profile.last_name?.trim()?.[0] || ''
  const initials = `${first}${last}`.toUpperCase()
  return initials || (profile.user.email?.[0] || '?').toUpperCase()
}

/**
 * The sign-in email on `user` is the identity a password reset keys on;
 * `profile.email` is the profile record's own column and can drift from it.
 */
function accountEmail(profile: UserProfile): string {
  return profile.user.email?.trim() || profile.email?.trim() || ''
}

export function AccountMenu() {
  const { profile, logout } = useAuth()
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Without this, Escape leaves focus on a node React has just unmounted,
      // which drops a keyboard user back at the document root.
      triggerRef.current?.focus()
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Unreachable behind RequireAuth, which redirects on a null profile. Guarded
  // anyway so this never renders an account menu with no account.
  if (!profile) return null

  const name = accountName(profile)
  const email = accountEmail(profile)

  return (
    <div className="account-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="avatar account-menu__trigger"
        // The visible initials are not a name anyone can say, so the accessible
        // name carries the full one (WCAG 2.5.3).
        aria-label={`Account menu for ${name}`}
        aria-expanded={open}
        // Only while the panel exists: an aria-controls pointing at an absent
        // id is a broken reference some assistive tech reports rather than
        // ignores.
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {accountInitials(profile)}
      </button>

      {open && (
        <section className="account-menu__panel" id={panelId} aria-label="Account">
          <header className="account-menu__identity">
            <p className="account-menu__identity-name">{name}</p>
            {email && <p className="account-menu__identity-email">{email}</p>}
          </header>
          <div className="account-menu__actions">
            <Link className="account-menu__item" to="/profile" onClick={() => setOpen(false)}>
              <UserRound size={15} aria-hidden="true" /> Your profile
            </Link>
            <button type="button" className="account-menu__item" onClick={() => void logout()}>
              <LogOut size={15} aria-hidden="true" /> Sign out
            </button>
          </div>
        </section>
      )}
    </div>
  )
}

export default AccountMenu
